<template>
  <div class="kp-error">
    <div class="kp-error__shell">
      <div class="kp-error__frame">
        <header class="kp-error__top">
          <NuxtLink :to="links.home" class="kp-error__brand" aria-label="Kendrick home">
            <NuxtImg src="/brand-assets/kendrick_logo.svg?v=20260326" alt="Kendrick" class="kp-error__brand-mark" width="88" height="70" provider="passthrough" sizes="88px" />
          </NuxtLink>

          <nav class="kp-error__mini-nav" aria-label="Error navigation">
            <NuxtLink :to="links.store">Store</NuxtLink>
            <NuxtLink :to="links.magazine">Magazine</NuxtLink>
            <NuxtLink :to="links.contact">Contact</NuxtLink>
          </nav>
        </header>

        <main class="kp-error__body">
          <section class="kp-error__lead">
            <div class="kp-error__status-row">
              <p class="kp-error__eyebrow">
                {{ statusLabel }}
              </p>
              <p class="kp-error__status-badge">
                {{ statusCode }}
              </p>
            </div>
            <h1 class="kp-error__title">
              {{ title }}
            </h1>
            <p class="kp-error__copy">
              {{ description }}
            </p>

            <p v-if="showPath" class="kp-error__path">
              {{ displayPath }}
            </p>

            <p v-if="showCountdown" class="kp-error__countdown">
              Redirecting to home in {{ countdown }}s.
            </p>

            <div class="kp-error__actions">
              <NuxtLink :to="links.home" class="kp-error__button kp-error__button--solid">
                Return Home
              </NuxtLink>
              <NuxtLink :to="links.store" class="kp-error__button kp-error__button--ghost">
                Enter Store
              </NuxtLink>
            </div>
          </section>

          <aside class="kp-error__recovery" aria-label="Recovery paths">
            <video
              class="kp-error__visual"
              autoplay
              muted
              loop
              playsinline
              preload="metadata"
              poster="/video/ankleboots-poster.jpg"
              aria-hidden="true"
              tabindex="-1"
            >
              <source src="/video/ankleboots-540.mp4" type="video/mp4">
            </video>

            <div class="kp-error__panel">
              <p class="kp-error__panel-label">Suggested paths</p>
              <ul class="kp-error__list" role="list">
                <li><NuxtLink :to="links.ankleBoots">ALIGN 110 Ankle Boot</NuxtLink></li>
                <li><NuxtLink :to="links.boots">AXIS 110 Boot</NuxtLink></li>
                <li><NuxtLink :to="links.magazine">Magazine</NuxtLink></li>
                <li><NuxtLink :to="links.brand">Brand</NuxtLink></li>
              </ul>
            </div>
          </aside>
        </main>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useLocalizedRoute } from '~/composables/useLocalizedRoute'

const error = useError()
const route = useRoute()
const { getLocalizedPath } = useLocalizedRoute()

const statusCode = computed(() => Number(error.value?.status ?? error.value?.statusCode ?? 404))
const is404 = computed(() => statusCode.value === 404)
const is500 = computed(() => statusCode.value >= 500)

const MAX_DISPLAY_PATH_LENGTH = 96
const ENCODED_PATH_RE = /%[0-9a-f]{2}/i
const NON_ASCII_RE = /[^\x20-\x7E]/

const currentPath = computed(() => route.fullPath || route.path || '/')
const displayPath = computed(() => {
  const path = currentPath.value.trim()
  if (!path || path === '/') return ''
  if (path.length > MAX_DISPLAY_PATH_LENGTH) return ''
  if (ENCODED_PATH_RE.test(path) || NON_ASCII_RE.test(path)) return ''
  return path
})
const showPath = computed(() => is404.value && displayPath.value !== '')
const redirectDelaySeconds = computed(() => {
  if (is404.value) return 10
  return 0
})
const showCountdown = computed(() => redirectDelaySeconds.value > 0)

const links = computed(() => ({
  home: getLocalizedPath('/'),
  store: getLocalizedPath('/store'),
  magazine: getLocalizedPath('/magazine'),
  contact: getLocalizedPath('/contact'),
  brand: getLocalizedPath('/brand'),
  ankleBoots: getLocalizedPath('/ankle-boots'),
  boots: getLocalizedPath('/boots')
}))

const statusLabel = computed(() => {
  if (is500.value) return 'Temporary interruption'
  if (is404.value) return '404'
  return `Error ${statusCode.value}`
})

const title = computed(() => {
  if (is500.value) return 'Temporarily unavailable.'
  if (is404.value) return 'Page not found.'
  return 'This page is unavailable.'
})

const description = computed(() => {
  if (is500.value) {
    return 'The request did not complete correctly. Use a core path below or try again in a moment.'
  }

  if (is404.value) {
    return 'This address is no longer active, or was never published. Use one of the core paths below.'
  }

  return 'The requested page is currently unavailable.'
})

const countdown = ref(redirectDelaySeconds.value || 10)
let countdownInterval: ReturnType<typeof setInterval> | null = null

onMounted(() => {
  if (!showCountdown.value) return

  countdown.value = redirectDelaySeconds.value

  countdownInterval = setInterval(() => {
    countdown.value -= 1

    if (countdown.value <= 0) {
      if (countdownInterval) {
        clearInterval(countdownInterval)
        countdownInterval = null
      }

      clearError({ redirect: links.value.home })
    }
  }, 1000)
})

onUnmounted(() => {
  if (countdownInterval) {
    clearInterval(countdownInterval)
    countdownInterval = null
  }
})

useHead({
  title: computed(() => {
    if (is404.value) return '404 - Page Not Found'
    if (is500.value) return 'Kendrick Paris'
    return `${statusCode.value}`
  }),
  meta: [
    {
      name: 'description',
      content: computed(() => {
        if (is404.value) return 'The requested Kendrick Paris page could not be found.'
        return 'Kendrick Paris designer footwear.'
      })
    },
    {
      name: 'robots',
      content: 'noindex, nofollow'
    }
  ]
})
</script>
